// Recognising, and recovering from, a build this device has fallen behind.
//
// Every deploy renames every chunk. A tab that was open across one - or an installed PWA holding an
// old index.html - asks for a filename the server no longer has. That is not really an error: the
// app is simply out of date, and a reload fetches fresh HTML and therefore the current names.
//
// The hard part is recognising it, because every engine words the failure differently, and Safari
// words it in a way that mentions neither chunks nor modules:
//
//   Chrome    Failed to fetch dynamically imported module: https://…/assets/index-abc.js
//   Firefox   error loading dynamically imported module
//   Safari    'text/html' is not a valid JavaScript MIME type.
//
// Safari's version is what a missing chunk looked like from behind an SPA fallback that answered
// with index.html. The server no longer does that, but the wording stays on this list: an old
// service worker can still hand a navigation a cached shell, and a message we fail to recognise
// costs the user a dead end instead of a reload.

const STALE_BUILD =
  /dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \S+ failed|valid JavaScript MIME type|Expected a JavaScript module script|Failed to load module script|error loading dynamically imported module/i;

const RELOAD_KEY = 'av:chunk-reload-at';
/** Long enough to cover a slow reload, short enough that a later deploy heals itself again. */
const RELOAD_COOLDOWN_MS = 20_000;

/** True if this failure is the shape a superseded build produces. */
export function isStaleBuildError(message: string): boolean {
  return STALE_BUILD.test(message);
}

/** True if we reloaded very recently - i.e. reloading again would just loop. */
export function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    return Number.isFinite(at) && Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    return false; // private mode / storage blocked: better to try the reload than to give up
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* storage unavailable - the cooldown just won't apply */
  }
}

/**
 * Drop every cache this device holds, then reload onto the current build.
 *
 * The caches go first because the reload can otherwise be served the same stale index.html by the
 * service worker, which would leave the tab exactly where it started.
 */
export function reloadOntoCurrentBuild(): void {
  markReloaded();
  void (async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      /* best effort */
    } finally {
      window.location.reload();
    }
  })();
}

/**
 * Catch a failed lazy import anywhere in the app, not only inside a route boundary.
 *
 * Vite fires `vite:preloadError` the moment a chunk fails to load, which is earlier and more
 * reliable than pattern-matching whatever the engine eventually throws. The route boundary keeps
 * its own check as the backstop for everything this does not see.
 */
export function watchForStaleBuild(): void {
  window.addEventListener('vite:preloadError', (e) => {
    if (reloadedRecently()) return; // a broken deploy must not become a reload loop
    e.preventDefault();
    reloadOntoCurrentBuild();
  });

  // A new service worker taking control means a deploy has just landed. The worker is configured
  // to skip waiting and claim open pages, so from this moment every fetch is answered from the
  // new build - but the code already running in this tab is still the old one, and stays so until
  // the page reloads. An installed PWA has no address bar to reload from, so do it here, once.
  if ('serviceWorker' in navigator) {
    let hadController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController) {
        hadController = true; // very first install on this device - nothing is out of date
        return;
      }
      if (reloadedRecently()) return;
      markReloaded();
      window.location.reload();
    });
  }
}
